import React, { memo, useState, useEffect } from "react";
import styles from "../../viewer/UploadSlideStock.module.css";
import { pdfjs } from "react-pdf";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { useInitNumber } from "../../../components/function/useInitNumber";
import {
    GetSlidePreviewImageUrl,
    GetTempPreviewUrl,
} from "../../../store/actions/SlideActions";

interface SlideViewContentProps {
    pageNumber: number;
    setPageNumber: (pageNumber: number) => void;
    editFlg: boolean;
}

SwiperCore.use([Navigation]);
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function SlideViewContent({ pageNumber, setPageNumber, editFlg }: SlideViewContentProps) {
    // API CAll
    const dispatch = useDispatch();

    const {
        tempFile,
        tempFilePageCount,
        tempFilePreviewImage,
        slide,
        slidePageCount,
        slidePreviewImageUrl,
    } = useSelector((state: AppState) => ({
        tempFile: state.slide.tempFile,
        tempFilePageCount: state.slide.tempFilePageCount,
        tempFilePreviewImage: state.slide.tempFilePreviewImage,
        slide: state.slide.slide,
        slidePageCount: state.slide.slidePageCount,
        slidePreviewImageUrl: state.slide.slidePreviewImageUrl,
    }));

    // ファイルのページ合計数
    const [numPages, setNumPages] = useInitNumber();

    useEffect(() => {
        if (!editFlg) {
            // 新規コンテンツアップロードの場合、ファイルプレビュー用のURL、枚数取得して設定
            if (tempFilePageCount > 0) setNumPages(tempFilePageCount);
        } else {
            // 編集画面でファイル差し替えの場合、ファイルプレビュー用のURL、枚数取得して設定
            if (tempFilePageCount > 0 && tempFile.keyName) {
                setNumPages(tempFilePageCount);
            } else {
                // 編集画面でファイル差し替えがないの場合、ファイルプレビュー用のURL、枚数取得して設定
                if (slidePageCount > 0) setNumPages(slidePageCount);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tempFile, tempFilePageCount, slidePageCount]);

    //  ファイルプレビュー用のURL取得
    useEffect(() => {
        // コンテンツアップロード新規の場合、tempFilePageCountよりPreviewURL取得
        if (!editFlg) {
            if (tempFilePageCount > 0) {
                if (tempFilePreviewImage.length < tempFilePageCount) {
                    dispatch(GetTempPreviewUrl(tempFile.keyName, tempFilePreviewImage.length + 1));
                }
            }
        } else {
            // 編集画面でファイル差し替えの場合、tempFile.keyNameよりPreviewURL取得
            if (tempFilePageCount > 0 && tempFile.keyName) {
                if (tempFilePreviewImage.length < tempFilePageCount) {
                    dispatch(GetTempPreviewUrl(tempFile.keyName, tempFilePreviewImage.length + 1));
                }
            } else {
                // 編集画面でファイル差し替えがないの場合、slidePageCountよりPreviewURL取得
                if (slidePageCount > 0) {
                    if (slidePreviewImageUrl.length < slidePageCount) {
                        // Todo修正、slideIDを固定に設定
                        if (slide) {
                            dispatch(
                                GetSlidePreviewImageUrl(
                                    slide.id,
                                    slide.fileName,
                                    slidePreviewImageUrl.length + 1
                                )
                            );
                        }
                    }
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tempFilePageCount,
        tempFilePreviewImage.length,
        tempFile,
        slide,
        slidePageCount,
        slidePreviewImageUrl.length,
    ]);

    // 編集画面でファイル差し替えによって、画像表示フラグ定義
    const [editIsFileReplaced, setEditIsFileReplaced] = useState(false);
    useEffect(() => {
        if (editFlg) {
            setEditIsFileReplaced(false);
        }
        if (editFlg && tempFile.keyName) {
            setEditIsFileReplaced(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editFlg, tempFile.keyName]);

    // swiperを設定する
    const onSwiper = (swiper: SwiperCore) => {
        setCurrentSwiper(swiper);
    };

    //ページグループ
    const [currentSwiper, setCurrentSwiper] = useState<null | SwiperCore>(null);

    // navigationRef用
    const navigationPrevRef = React.useRef(null);
    const navigationNextRef = React.useRef(null);

    // 選択ページを変更
    function changePage(offset: number) {
        setPageNumber(pageNumber + offset);
    }

    // 選択ページを1ページ前に設定
    function handleClickPreviousPage() {
        if (!editFlg) {
            // コンテンツアップロード新規の場合、選択ページを1ページ前に設定
            if (tempFilePreviewImage.length > 0) {
                if (pageNumber > 1) {
                    changePage(-1);
                    if (currentSwiper) {
                        currentSwiper.slideTo(pageNumber - 2);
                    }
                }
            }
        } else {
            // 編集画面でファイル差し替えの場合、選択ページを1ページ前に設定・編集画面でファイル差し替えがない場合、選択ページを1ページ前に設定
            if (
                (tempFilePreviewImage.length > 0 && tempFile.keyName) ||
                slidePreviewImageUrl.length > 0
            ) {
                if (pageNumber > 1) {
                    changePage(-1);
                    if (currentSwiper) {
                        currentSwiper.slideTo(pageNumber - 2);
                    }
                }
            }
        }
    }

    // 選択ページを1ページ後に設定
    function handleClickNextPage() {
        if (!editFlg) {
            // コンテンツアップロード新規の場合、選択ページを1ページ後に設定
            if (tempFilePreviewImage.length > pageNumber) {
                if (pageNumber < numPages) {
                    changePage(1);
                    if (currentSwiper) {
                        currentSwiper.slideTo(pageNumber);
                    }
                }
            }
        } else {
            // 編集画面でファイル差し替えの場合、選択ページを1ページ後に設定・編集画面でファイル差し替えがない場合、選択ページを1ページ後に設定
            if (
                (tempFilePreviewImage.length > pageNumber && tempFile.keyName) ||
                slidePreviewImageUrl.length > pageNumber
            ) {
                if (pageNumber < numPages) {
                    changePage(1);
                    if (currentSwiper) {
                        currentSwiper.slideTo(pageNumber);
                    }
                }
            }
        }
    }

    return (
        <>
            {/* プレビュー枠*/}
            <div className={styles.pagePreview}>
                {/* プレビュー画像表示枠 */}
                <div className={styles.pageArea}>
                    {/* プレビュー画像枠 */}
                    <div className={styles.previewImage}>
                        {/* コンテンツアップロード新規の場合、tempFilePreviewImageよりイメージ表示 */}
                        {!editFlg && tempFilePreviewImage.length > 0 && (
                            <img
                                src={tempFilePreviewImage[pageNumber - 1].imageUrl}
                                className={styles.pagePosition}
                                alt=""
                            />
                        )}

                        {/* 編集画面でファイル差し替えの場合、tempFilePreviewImageよりイメージ表示 */}
                        {editFlg && editIsFileReplaced && tempFilePreviewImage.length > 0 && (
                            <img
                                src={tempFilePreviewImage[pageNumber - 1].imageUrl}
                                className={styles.pagePosition}
                                alt=""
                            />
                        )}

                        {/* 編集画面でファイル差し替えがない場合、slidePreviewImageUrlよりイメージ表示 */}
                        {editFlg && !editIsFileReplaced && slidePreviewImageUrl.length > 0 && (
                            <img
                                src={slidePreviewImageUrl[pageNumber - 1].imageUrl}
                                className={styles.pagePosition}
                                alt=""
                            />
                        )}
                    </div>
                </div>
            </div>
            {/* Pagination枠 */}
            <div className={styles.paginArea}>
                {/* pagin真ん中エリア */}
                <div className={styles.paginCenterArea}>
                    {/* leftArrowエリア */}
                    <div className={styles.leftArrowArea}>
                        <img
                            src={require("../../../assets/image/left.svg")}
                            onMouseOver={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/left_hover.svg"))
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/left.svg"))
                            }
                            alt="leftIcon"
                            className={styles.iconStyleLeftArrow}
                            onClick={handleClickPreviousPage}
                        />
                    </div>
                    {/* PageNumberエリア */}
                    <div className={styles.pageNumberArea}>
                        {pageNumber}/{numPages}
                    </div>
                    {/* rightArrowエリア */}
                    <div className={styles.rightArrowArea}>
                        <img
                            src={require("../../../assets/image/right.svg")}
                            onMouseOver={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/right_hover.svg"))
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/right.svg"))
                            }
                            alt="rightIcon"
                            className={styles.iconStyleRightArrow}
                            onClick={handleClickNextPage}
                        />
                    </div>
                </div>
            </div>
            {/* 画像リスト枠 */}
            <div className={styles.pageSlideArea}>
                <div className={styles.pagePreviewLeftLarge}>
                    <div ref={navigationPrevRef}>
                        <img
                            src={require("../../../assets/image/left2.svg")}
                            onMouseOver={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/left2_hover.svg"))
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/left2.svg"))
                            }
                            alt="leftArrowIcon"
                            className={styles.iconStyle}
                        />
                    </div>
                </div>
                <div className={styles.pageSlidelist}>
                    <Swiper
                        onSwiper={(swiper) => onSwiper(swiper)}
                        spaceBetween={5}
                        slidesPerGroup={5}
                        slidesPerView={5}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        style={{ height: "96px" }}
                    >
                        {/* コンテンツアップロード新規の場合、tempFilePreviewImageよりスライドビューイメージ表示 */}
                        {!editFlg &&
                            tempFilePreviewImage.map((image, index: number) => (
                                <SwiperSlide
                                    key={index}
                                    className={
                                        pageNumber !== index + 1 ? styles.slide : styles.swiperSlide
                                    }
                                    onClick={() => {
                                        setPageNumber(index + 1);
                                    }}
                                >
                                    <img
                                        src={image.imageUrl}
                                        alt=""
                                        className={styles.slideImage}
                                    />
                                </SwiperSlide>
                            ))}

                        {/* 編集画面でファイル差し替えの場合、tempFilePreviewImageよりスライドビューイメージ表示 */}
                        {editFlg &&
                            editIsFileReplaced &&
                            tempFilePreviewImage.map((image, index: number) => (
                                <SwiperSlide
                                    key={index}
                                    className={
                                        pageNumber !== index + 1 ? styles.slide : styles.swiperSlide
                                    }
                                    onClick={() => {
                                        setPageNumber(index + 1);
                                    }}
                                >
                                    <img
                                        src={image.imageUrl}
                                        alt=""
                                        className={styles.slideImage}
                                    />
                                </SwiperSlide>
                            ))}

                        {/* 編集画面でファイル差し替えがない場合、slidePreviewImageUrlよりスライドビューイメージ表示 */}
                        {editFlg &&
                            !editIsFileReplaced &&
                            slidePreviewImageUrl.map((image, index: number) => (
                                <SwiperSlide
                                    key={index}
                                    className={
                                        pageNumber !== index + 1 ? styles.slide : styles.swiperSlide
                                    }
                                    onClick={() => {
                                        setPageNumber(index + 1);
                                    }}
                                >
                                    <img
                                        src={image.imageUrl}
                                        alt=""
                                        className={styles.slideImage}
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                </div>
                <div className={styles.pagePreviewRightLarge}>
                    <div ref={navigationNextRef}>
                        <img
                            src={require("../../../assets/image/right2.svg")}
                            onMouseOver={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/right2_hover.svg"))
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.src = require("../../../assets/image/right2.svg"))
                            }
                            alt="rightArrowIcon1"
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default memo(SlideViewContent);
